import { QueryClient } from "@tanstack/react-query";

import { QUERY_DEFAULTS } from "@/core/constants";

import { ApiError } from "./errors";

/** Server data lives in memory only, with explicit freshness and eviction (DECISIONS.md §2.8). */
export function createQueryClient(): QueryClient {
  return new QueryClient({
    defaultOptions: {
      queries: {
        staleTime: QUERY_DEFAULTS.staleTimeMs,
        gcTime: QUERY_DEFAULTS.gcTimeMs,
        retry: (failures, error) =>
          failures < QUERY_DEFAULTS.maxRetries && error instanceof ApiError && error.retryable,
        refetchOnWindowFocus: true,
      },
      // Changes are never retried blindly: money calls carry an Idempotency-Key and are retried on purpose.
      mutations: { retry: false },
    },
  });
}

/**
 * Query keys in one place, so a change can invalidate exactly what it affects. Everything about one brand
 * starts with `brand(brandId)`, so leaving a brand (or switching) can drop it in one go.
 */
export const QUERY_KEYS = {
  me: ["me"] as const,
  /** The person's brand-team inbox (across their brands). */
  notifications: ["notifications"] as const,
  myBrands: ["my-brands"] as const,
  invitations: ["invitations"] as const,
  brand: (brandId: string) => ["brand", brandId] as const,
  reportSummary: (brandId: string, range: { from?: string; to?: string } = {}) =>
    ["brand", brandId, "report-summary", range] as const,
  analytics: {
    summary: (brandId: string, range: string) => ["brand", brandId, "analytics", "summary", range] as const,
    day: (brandId: string, date: string) => ["brand", brandId, "analytics", "day", date] as const,
    month: (brandId: string, month: string) => ["brand", brandId, "analytics", "month", month] as const,
    plans: (brandId: string) => ["brand", brandId, "analytics", "plans"] as const,
  },
  dispatch: {
    all: (brandId: string) => ["brand", brandId, "dispatch"] as const,
    sheet: (brandId: string, date: string) => ["brand", brandId, "dispatch", "sheet", date] as const,
    templates: (brandId: string) => ["brand", brandId, "dispatch", "templates"] as const,
  },
  customers: {
    list: (brandId: string, filters: { search?: string; status?: string }) =>
      ["brand", brandId, "customers", "list", filters] as const,
    detail: (brandId: string, subscriptionId: string, from?: string) =>
      ["brand", brandId, "customers", "detail", subscriptionId, from ?? "start"] as const,
  },
  reviews: (brandId: string, rating?: number) => ["brand", brandId, "reviews", rating ?? "any"] as const,
  mealLog: (brandId: string, filters: object) => ["brand", brandId, "meal-log", filters] as const,
  brandCustomers: {
    list: (brandId: string, search?: string) =>
      ["brand", brandId, "brand-customers", "list", search ?? ""] as const,
    detail: (brandId: string, userId: string) =>
      ["brand", brandId, "brand-customers", "detail", userId] as const,
  },
  dishes: (brandId: string) => ["brand", brandId, "dishes"] as const,
  plans: (brandId: string) => ["brand", brandId, "plans"] as const,
  menus: {
    all: (brandId: string) => ["brand", brandId, "menus"] as const,
    week: (brandId: string, planId: string, monday: string) =>
      ["brand", brandId, "menus", "week", planId, monday] as const,
    templates: (brandId: string) => ["brand", brandId, "menus", "templates"] as const,
  },
  brandSettings: (brandId: string) => ["brand", brandId, "settings"] as const,
  mealSlots: (brandId: string) => ["brand", brandId, "meal-slots"] as const,
  promoCodes: (brandId: string) => ["brand", brandId, "promo-codes"] as const,
  team: (brandId: string) => ["brand", brandId, "team"] as const,
  money: {
    earnings: (brandId: string, from: string, to: string) =>
      ["brand", brandId, "money", "earnings", from, to] as const,
    payouts: (brandId: string, status?: string) =>
      ["brand", brandId, "money", "payouts", status ?? "all"] as const,
    payout: (brandId: string, payoutId: string) => ["brand", brandId, "money", "payout", payoutId] as const,
    account: (brandId: string) => ["brand", brandId, "money", "account"] as const,
  },
  holidays: (brandId: string, from: string, to: string) => ["brand", brandId, "holidays", from, to] as const,
};
